import { Box, Button, Container, Grid, IconButton, Typography } from "@mui/material";
import React from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaChartLine,
  FaClipboardList,
  FaLock,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const T = {
  sky: "#0EA5E9",
  skyDeep: "#0284C7",
  skyLight: "#BAE6FD",
  skySoft: "rgba(14,165,233,0.08)",
  ink: "#0C1A2E",
  inkMid: "#374151",
  inkLight: "#6B7280",
  inkFaint: "#94A3B8",
  violet: "#7C3AED",
  violetLight: "rgba(124,58,237,0.10)",
  surface: "#FFFFFF",
  surfaceAlt: "#F8FAFC",
  border: "rgba(14,165,233,0.12)",
  borderSolid: "#E2E8F0",
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

/** Feature card (Performance / Assignments) */
const FeatureCard = ({
  icon: Icon,
  title,
  description,
  cta,
  onClick,
  accent = T.sky,
  outlined = false,
  index = 0,
}) => (
  <motion.div
    variants={fadeUp}
    custom={index}
    initial="hidden"
    animate="show"
    style={{ height: "100%" }}
  >
    <Box
      sx={{
        height: "100%",
        p: { xs: 3, md: 3.5 },
        borderRadius: "20px",
        background: T.surface,
        border: `1px solid ${T.borderSolid}`,
        boxShadow: "0 2px 16px rgba(12,26,46,0.05)",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        position: "relative",
        overflow: "hidden",
        transition: "box-shadow 0.3s, transform 0.3s",
        "&:hover": {
          boxShadow: "0 10px 32px rgba(14,165,233,0.14)",
          transform: "translateY(-4px)",
        },
      }}
    >
      
      {/* top accent bar */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: outlined
            ? `linear-gradient(90deg, ${T.violet}, #A78BFA)`
            : `linear-gradient(90deg, ${T.sky}, ${T.skyDeep})`,
        }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            background: outlined ? T.violetLight : T.skySoft,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={20} color={outlined ? T.violet : T.skyDeep} />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            px: 1.2,
            py: 0.5,
            borderRadius: "999px",
            background: T.surfaceAlt,
            border: `1px solid ${T.borderSolid}`,
          }}
        >
          <FaLock size={10} color={T.inkFaint} />
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              color: T.inkFaint,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Login required
          </Typography>
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: "'Syne', sans-serif",
          fontWeight: 700,
          fontSize: "1.05rem",
          color: T.ink,
          lineHeight: 1.25,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          color: T.inkLight,
          fontSize: "0.88rem",
          lineHeight: 1.65,
          flex: 1,
        }}
      >
        {description}
      </Typography>

      <Button
        variant={outlined ? "outlined" : "contained"}
        onClick={onClick}
        endIcon={<FaArrowRight size={11} />}
        sx={{
          alignSelf: "flex-start",
          mt: 0.5,
          borderRadius: "10px",
          px: 2.5,
          py: 0.9,
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 700,
          fontSize: "0.82rem",
          textTransform: "none",
          letterSpacing: "0.2px",
          ...(outlined
            ? {
                borderColor: T.violet,
                color: T.violet,
                "&:hover": {
                  borderColor: "#6D28D9",
                  background: T.violetLight,
                },
              }
            : {
                background: `linear-gradient(135deg, ${T.sky}, ${T.skyDeep})`,
                boxShadow: "0 4px 14px rgba(14,165,233,0.32)",
                color: "#fff",
                border: "none",
                "&:hover": {
                  background: `linear-gradient(135deg, ${T.skyDeep}, #0369A1)`,
                  boxShadow: "0 6px 18px rgba(14,165,233,0.4)",
                },
              }),
        }}
      >
        {cta}
      </Button>
    </Box>
  </motion.div>
);

const StudentPortal = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        background: T.surfaceAlt,
        py: { xs: 6, md: 9 },
        px: { xs: 2, md: 0 },
        height: "100vh",
      }}
    >
      <Container maxWidth="md">
        {/* Back button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          style={{ position: "absolute", top: 24, left: 24, zIndex: 10 }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: "#475569",
              background: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(8px)",
              boxShadow:
                "0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)",
              transition: "all 0.2s ease",
              "&:hover": {
                background: "#fff",
                color: "#0f172a",
                transform: "translateX(-2px)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              },
            }}
          >
            <FaArrowLeft size={15} />
          </IconButton>
        </motion.div>
        <motion.div
          variants={fadeUp}
          custom={0}
          initial="hidden"
          animate="show"
          style={{ textAlign: "center", marginBottom: 40 }}
        >
          <Typography
            sx={{
              display: "inline-block",
              mb: 1.5,
              px: 1.6,
              py: 0.5,
              borderRadius: "999px",
              background: T.skySoft,
              color: T.skyDeep,
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 700,
              fontSize: "0.75rem",
              letterSpacing: "0.4px",
              textTransform: "uppercase",
            }}
          >
            Student Portal
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
              fontSize: { xs: "1.6rem", md: "2rem" },
              color: T.ink,
              mb: 1,
            }}
          >
            Your Academic Dashboard
          </Typography>
          <Typography
            sx={{
              color: T.inkLight,
              fontSize: "0.95rem",
              maxWidth: 480,
              mx: "auto",
              lineHeight: 1.65,
            }}
          >
            Sign in to track your progress, review results, and stay on top of
            every assignment in one secure place.
          </Typography>
        </motion.div>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FeatureCard
              icon={FaChartLine}
              title="Performance Analysis"
              description="View detailed grade breakdowns, term reports, and progress trends tailored to your learning journey."
              cta="Login to View"
              onClick={() => navigate("/regular-student-login")}
              accent={T.violet}
              outlined
              index={1}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FeatureCard
              icon={FaClipboardList}
              title="Student Assignments"
              description="Check pending and submitted assignments, due dates, and feedback from your teachers."
              cta="Login to View"
              onClick={() => navigate("/student-assignment-login")}
              accent={T.violet}
              outlined
              index={2}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default StudentPortal;
